import { useEffect, useState } from "react";
import { api, type Project, type ResearchCandidate } from "../api/client";
import Icon from "../components/Icon";
import { useDraft } from "../hooks/useDraft";
import { getCurrentProject } from "../hooks/useCurrentProject";
import { profileForStudy } from "../data/studyFixtures";

const CATEGORIES = [
  "irb", "sampling", "instruments", "documentation", "data_security",
  "training", "fsl_framework", "livelihoods_framework", "methods_general",
];

export default function ResearchAgentPage() {
  const [query, setQuery] = useDraft<string>("abby_draft:research:query", "");
  const [busy, setBusy] = useState(false);
  const [candidates, setCandidates] = useState<ResearchCandidate[]>([]);
  const [runId, setRunId] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [category, setCategory] = useState("methods_general");
  const [targetStudy, setTargetStudy] = useState<Project | null>(null);
  const [ingestResults, setIngestResults] = useState<{ url: string | null; ok: boolean; title?: string; error?: string }[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const cp = getCurrentProject();
    if (cp.kind !== "project") {
      setTargetStudy(null);
      return;
    }
    api
      .listProjects(true)
      .then((projects) => setTargetStudy(projects.find((p) => p.id === cp.id) ?? null))
      .catch(() => {});
  }, []);

  async function find() {
    if (!query.trim()) return;
    setBusy(true);
    setError(null);
    setCandidates([]);
    setIngestResults(null);
    setSelected(new Set());
    try {
      const r = await api.researchFind(query.trim());
      setRunId(r.run_id);
      setCandidates(r.candidates);
      if (r.candidates.length === 0) setError("No candidates returned. Anthropic web_search may be unavailable on this API tier and TAVILY_API_KEY isn't set as a fallback.");
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  function toggle(i: number) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  }

  async function ingestSelected() {
    if (runId == null || selected.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.researchIngest(runId, [...selected], category);
      setIngestResults(r.results);
      setSelected(new Set());
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="main-header">
        <div>
          <span className="crumb">{targetStudy ? `/ studies / ${targetStudy.name} / research` : "/ research"}</span>
          <h2>Research agent</h2>
        </div>
      </div>
      <div className="main-body">
        {targetStudy && (
          <div className="research-target">
            <span className="project-dot" style={{ background: targetStudy.color || "var(--accent)" }} />
            <span className="lab">ingest target</span>
            <strong>{targetStudy.name}</strong>
            <span>{profileForStudy(targetStudy).phase}</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}
        <div className="row research-query-row">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Describe the sources or literature you want to find"
            onKeyDown={(e) => e.key === "Enter" && find()}
          />
          <button className="primary" onClick={find} disabled={busy || !query.trim()}>
            {busy ? <><span className="spinner" />Searching...</> : <><Icon name="research" size={15} />Find sources</>}
          </button>
        </div>
        {error && <p className="error">{error}</p>}

        {candidates.length > 0 && (
          <>
            <p className="muted" style={{ maxWidth: 900 }}>
              Tick the sources you want to add to the library, pick a category, then ingest.
            </p>
            {candidates.map((c, i) => (
              <div key={i} className="candidate research-candidate">
                <div className="head">
                  <input type="checkbox" checked={selected.has(i)} onChange={() => toggle(i)} />
                  <label>
                    <div>
                      <div className="title">{c.title}</div>
                      <div className="meta">
                        {c.authors && <span>{c.authors}</span>}
                        {c.year && <span>· {c.year}</span>}
                        {c.license_likelihood && <span className="tag">{c.license_likelihood}</span>}
                      </div>
                      <div className="url"><a href={c.url} target="_blank" rel="noreferrer">{c.url}</a></div>
                      {c.abstract && <div style={{ marginTop: 6 }}>{c.abstract}</div>}
                      {c.why_relevant && <div className="muted" style={{ marginTop: 4, fontSize: 13 }}>Why relevant: {c.why_relevant}</div>}
                    </div>
                  </label>
                </div>
              </div>
            ))}
            <div className="row" style={{ marginTop: 12, maxWidth: 900 }}>
              <span>Category for ingested sources:</span>
              <select value={category} onChange={(e) => setCategory(e.target.value)} style={{ width: 220 }}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <span className="spacer" />
              <button className="primary" onClick={ingestSelected} disabled={selected.size === 0 || busy}>
                {busy ? <><span className="spinner" />Ingesting...</> : `Ingest ${selected.size} selected`}
              </button>
            </div>
          </>
        )}

        {ingestResults && (
          <div style={{ marginTop: 24, maxWidth: 900 }}>
            <h3>Ingest results</h3>
            <ul>
              {ingestResults.map((r, i) => (
                <li key={i}>
                  {r.ok ? "✓" : "✗"} {r.title ?? r.url} {r.error && <span className="error">— {r.error}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  );
}
